import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/ui/Toast';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import PasswordInput, { isPasswordValid } from '../components/ui/PasswordInput';
import api from '../lib/api';

const STEPS = { PHONE: 1, OTP: 2, PASSWORD: 3 };

export default function ForgotPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();

  const [step, setStep] = useState(STEPS.PHONE);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Step 1
  const [phoneNumber, setPhoneNumber] = useState('');

  // Step 2
  const [otp, setOtp] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Step 3
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const res = await api.post('/auth/forgot-password', {
        phone_number: phoneNumber,
      });

      // In dev mode, show OTP in toast for convenience
      if (res.data?.dev_mode && res.data?.otp) {
        toast.info(`Dev mode OTP: ${res.data.otp}`);
      }

      setStep(STEPS.OTP);
    } catch (err) {
      setError(err.data?.error?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    try {
      const res = await api.post('/auth/verify-reset-otp', {
        phone_number: phoneNumber,
        otp,
      });

      setResetToken(res.data?.reset_token);
      setStep(STEPS.PASSWORD);
    } catch (err) {
      setError(t('auth.invalidOtp'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);

    if (newPassword !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (newPassword.length < 8) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    setIsLoading(true);

    try {
      await api.post('/auth/reset-password', {
        reset_token: resetToken,
        new_password: newPassword,
      });

      toast.success(t('auth.passwordResetSuccess'));
      navigate('/login', { replace: true });
    } catch (err) {
      setError(err.data?.error?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="text-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">
          {t('auth.forgotPasswordTitle')}
        </h2>
        <p className="text-sm text-gray-500 mt-1">
          {t('auth.forgotPasswordSubtitle')}
        </p>
        <p className="text-xs text-gray-400 mt-2">
          {t('auth.step')} {step} {t('auth.of')} 3
        </p>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-2xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Step 1: Phone Number */}
      {step === STEPS.PHONE && (
        <form onSubmit={handleSendOtp} className="space-y-4">
          <Input
            label={t('auth.phoneNumber')}
            type="tel"
            value={phoneNumber}
            onChange={(e) => setPhoneNumber(e.target.value)}
            placeholder={t('auth.phonePlaceholder')}
            required
            autoFocus
            dir="ltr"
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={isLoading}
          >
            {t('auth.sendOtp')}
          </Button>
        </form>
      )}

      {/* Step 2: OTP Verification */}
      {step === STEPS.OTP && (
        <form onSubmit={handleVerifyOtp} className="space-y-4">
          <p className="text-sm text-green-600 bg-green-50 border border-green-200 rounded-2xl p-3">
            {t('auth.otpSent')}
          </p>

          <Input
            label={t('auth.enterOtp')}
            type="text"
            value={otp}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').slice(0, 6);
              setOtp(val);
            }}
            placeholder={t('auth.otpPlaceholder')}
            required
            autoFocus
            dir="ltr"
            maxLength={6}
          />

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={isLoading}
            disabled={otp.length !== 6}
          >
            {t('auth.verifyOtp')}
          </Button>

          <button
            type="button"
            onClick={() => {
              setStep(STEPS.PHONE);
              setOtp('');
              setError(null);
            }}
            className="w-full text-sm text-gray-500 hover:text-gray-700"
          >
            {t('auth.sendOtp')}
          </button>
        </form>
      )}

      {/* Step 3: New Password */}
      {step === STEPS.PASSWORD && (
        <form onSubmit={handleResetPassword} className="space-y-4">
          <PasswordInput
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            label={t('auth.newPassword')}
            required
          />

          <Input
            label={t('auth.confirmPassword')}
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('auth.confirmPassword')}
            required
          />
          {confirmPassword && newPassword !== confirmPassword && (
            <p className="text-xs text-red-500 -mt-2">{t('auth.passwordsDoNotMatch')}</p>
          )}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="w-full"
            loading={isLoading}
            disabled={!isPasswordValid(newPassword) || newPassword !== confirmPassword}
          >
            {t('auth.resetPassword')}
          </Button>
        </form>
      )}

      {/* Back to Login */}
      <div className="mt-4 text-center">
        <Link
          to="/login"
          className="text-sm text-primary-600 hover:text-primary-700 font-medium"
        >
          {t('auth.backToLogin')}
        </Link>
      </div>
    </div>
  );
}
